import {
    Product,
    ProductOption,
    OctoProduct,
    Image,
    OctoSupplier,
    BilberrySettings,
    ProductInstance,
    OctoCalendarEntry,
    OctoCalendarRequest,
    OctoProductUnit,
    TicketOption,
    OctoAvailabilityEntry,
    OctoAvailabilityRequest,
} from '@repo/types';
import { tzdate } from '@repo/tzdate';

export function productsFromOctoProducts(products: OctoProduct[]): Product[] {
    return products.map(productFromOctoProduct);
}

export function productFromOctoProduct(product: OctoProduct): Product {
    // TODO: Improve image mapping
    const images =
        product.media?.map((m) => {
            return {
                src: m.src,
                alt: `${m.title}: ${m.caption}`,
            } as Image;
        }) ?? [];

    const options = product.options ?? [];

    // Map product options for UI grouping
    const productOptions: ProductOption[] = options.map((option) => ({
        id: option.id,
        title: option.title,
        description: option.description,
        shortDescription: option.shortDescription,
        internalName: option.internalName,
    }));

    // Flatten tickets but preserve option reference via productOptionId
    const allTickets = options.flatMap((option) =>
        (option.units ?? []).map((unit) => ticketOptionFromUnit(unit, undefined, option.id)),
    );

    const startTimes = options.flatMap((option) => option.availabilityLocalStartTimes ?? []);

    const missingCoverImage: Image = {
        src: 'https://bilberry.imgix.net/assets/missing-image.png',
        alt: 'Missing image',
    };

    const media = product.media ?? [];
    const coverMedia = media.find((img) => img.rel === 'LOGO');
    const coverImage = coverMedia
        ? {
              src: coverMedia.src,
              alt: `${coverMedia.title}: ${coverMedia.caption}`,
          }
        : missingCoverImage;

    return {
        id: product.id.toString(),
        name: product.title ?? '<Missing title>',
        type: 'timepoint',
        description: product.description ?? '<Missing description>',
        title: product.title ?? '<Missing title>',
        capacity: 99, // TODO: We don't get this from Octo
        duration: product.duration,
        images: images,
        shortDescription: product.shortDescription ?? '<Missing short description>',
        coverImage: coverImage,
        currency: 'NOK', // TODO
        difficulty: 'easy', // TODO
        fromAge: 0, // TODO
        longDescription: product.description,
        additionalInfo: '', // TODO
        startTimes: startTimes.join(', '),
        url: `./${product.slug}`, // TODO
        icons: null, // TODO
        ticketOptions: allTickets,
        fromPrice: null, // TODO
        location: null,
        departureLocation: null,
        minEntrants: 0, // TODO
        terms: '', // TODO
        pkgId: null,
        pkgTitle: null,
        pkgProductId: null,
        pkgTicketTypeId: null,
        pkgTicketOptionId: null,
        pkgTicketOptionName: null,
        pkgMinutesRelativeStart: null,
        pkgMinutesRelativeEnd: null,
        pkgTicketQuantity: null,
        accommodationAttributes: null,
        cutoffTime: null,
        productOptions: productOptions.length > 0 ? productOptions : undefined,
    } as Product;
}

export function settingsFromOctoSupplier(data: OctoSupplier): BilberrySettings {
    // TODO: Mapping of stuff we don't get from Octo
    return {
        name: data.name,
        email: '',
        phone: data.contact.telephone,
        address: data.contact.address,
        website: data.contact.website,
        zip: '5001',
        city: 'Bergen',
        country: 'Norway',
        logo: 'https://tripkit.fra1.digitaloceanspaces.com/Bilberries/strauman-adventure/settings/67/logo.png',
        currency: 'NOK',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        giftcard_dummy_image:
            // eslint-disable-next-line max-len,
            'https://tripkit.fra1.digitaloceanspaces.com/Bilberries/strauman-adventure/settings/70/mWdKcyBTMFsY1YfkqQJFiMP7rXSSkj-metaRXhwbG9yaXVtLXBpbXAtZ2lmdGNhcmQucG5n-.png',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        giftcard_expires_at: '2026-09-19T21:59:59.999999Z',
        timezone: 'Europe/Oslo',
    };
}

export function productInstancesFromOctoCalendarEntry(
    entry: OctoCalendarEntry,
    request: OctoCalendarRequest,
    product: OctoProduct,
): ProductInstance[] {
    const options = product.options ?? [];

    const option = options.find((o) => o.id.toString() === request.optionId);

    const tickets = option?.units ?? [];

    return entry.openingHours.map((openingHour) => {
        const productInstance = {
            id: `${request.productId}-${request.optionId}-${entry.localDate}-${openingHour.from}-${openingHour.to}`,
            productOptionId: request.optionId,
            productInstanceCollectionId: null,
            productInstanceCollectionMinEntrants: null,
            productInstanceCollectionMaxEntrants: null,
            productCollectionTitle: null,
            productCollectionShowTime: false,
            productCollectionShowTitle: false,
            productInstanceCollectionMaxEntrantsPerBooking: null,
            productInstanceCollectionValidateAll: false,
            collectedIds: [],
            capacity: entry.capacity,
            currentConsumedCapacity: entry.capacity - entry.vacancies,
            personsPerUnit: 1, // TODO: We don't get this from Octo
            cancellationDeadline: 0,
            cutoffDate: null,
            cutoffTime: null,
            start: tzdate(`${entry.localDate}T${openingHour.from}`),
            end: tzdate(`${entry.localDate}T${openingHour.to}`),
            guestListQuestionnaire: [],
            isClosedForBooking: false,
            orderQuestionnaire: [],
            product: null,
            requiresGuestListQuestionnaire: false,
            requiresOrderQuestionnaire: false,
            minEntrants: 0,
            minProductsRequiredToBook: null,
            ticketOptions: [],
            timeslots: [],
            relatedProducts: [],
            extraProducts: [],
            showExtrasAsMinimal: false,
            showExtrasAsList: false,
            showTime: true,
            isExtraProduct: false,
            icons: [],
        } as ProductInstance;

        (productInstance as any).ticketOptions = tickets.map((x) =>
            ticketOptionFromUnit(x, [productInstance], request.optionId),
        );

        return productInstance;
    });
}

export function productInstancesFromOctoAvailabilityEntry(
    entry: OctoAvailabilityEntry,
    request: OctoAvailabilityRequest,
    product: OctoProduct,
): ProductInstance {
    const options = product.options ?? [];

    const option = options.find((o) => o.id.toString() === request.optionId);

    const tickets = option?.units ?? [];

    const productInstance = {
        id: entry.id,
        productOptionId: request.optionId,
        productInstanceCollectionId: null,
        productInstanceCollectionMinEntrants: null,
        productInstanceCollectionMaxEntrants: null,
        productCollectionTitle: null,
        productCollectionShowTime: false,
        productCollectionShowTitle: false,
        productInstanceCollectionMaxEntrantsPerBooking: null,
        productInstanceCollectionValidateAll: false,
        collectedIds: [],
        capacity: entry.capacity,
        currentConsumedCapacity: entry.capacity - entry.vacancies,
        personsPerUnit: 1, // TODO: We don't get this from Octo
        cancellationDeadline: 0,
        cutoffDate: null,
        cutoffTime: null,
        start: tzdate(entry.localDateTimeStart),
        end: tzdate(entry.localDateTimeEnd),
        guestListQuestionnaire: [],
        isClosedForBooking: false,
        orderQuestionnaire: [],
        product: null,
        requiresGuestListQuestionnaire: false,
        requiresOrderQuestionnaire: false,
        minEntrants: 0,
        minProductsRequiredToBook: null,
        ticketOptions: [],
        timeslots: [],
        relatedProducts: [],
        extraProducts: [],
        showExtrasAsMinimal: false,
        showExtrasAsList: false,
        showTime: true,
        isExtraProduct: false,
        icons: [],
    } as ProductInstance;

    (productInstance as any).ticketOptions = tickets.map((x) =>
        ticketOptionFromUnit(x, [productInstance], request.optionId),
    );

    return productInstance;
}

function ticketOptionFromUnit(
    unit: OctoProductUnit,
    productInstances?: ProductInstance[],
    productOptionId?: string,
): TicketOption {
    const price =
        unit.pricingFrom && unit.pricingFrom.length > 0
            ? (unit.pricingFrom.find((p) => p.currency === 'NOK') ?? unit.pricingFrom[0]) // Find the price for the currency we're using
            : null;

    const dividePriceBy = price ? Math.pow(10, price.currencyPrecision) : 1;

    return {
        id: unit.id.toString(),
        defaultId: unit.id.toString(), // What's this used for?
        ticketCategoryId: unit.reference, // What's this used for?
        price: price ? price.retail / dividePriceBy : 0,
        depositAmount: null,
        vatAmount: price ? (price.retail - price.net) / dividePriceBy : 0,
        vatBreakdown: price
            ? price.includedTaxes.map((rate) => ({
                  rate: parseFloat(rate.name.replace('%', '')), // TODO: breeze returns a rate name like "25%". We should not require a number"
                  amount: rate.net / dividePriceBy,
                  vatAmount: (rate.retail - rate.net) / dividePriceBy,
              }))
            : [],
        name: unit.shortDescription,
        occupancy: 1, // How does octo deal with this?
        guests: 1,
        capacity: Number.MAX_VALUE,
        currentConsumedCapacity: 0,
        minEntrants: unit.restrictions.minQuantity ?? null,
        fromAge: null,
        toAge: null,
        productInstances: productInstances ?? [],
        ticketTypes: [],
        extras: unit.extras,
        productOptionId,
        unitType: unit.type,
    } as TicketOption;
}
